import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faPlug } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Spinner } from "@/components/Spinner/Spinner";
import {
	useGetMcpConnectionsQuery,
	useRevokeMcpConnectionMutation,
} from "@/pages/Private/redux/mcpOauth/mcpOauth.api";

import { SettingsCard } from "./SettingsCard";

/**
 * The AI applications this user has connected over MCP (docs/brief-mcp-server.md §"Tier 4") —
 * Claude, ChatGPT or any other MCP client that completed the OAuth flow. Each one acts as this
 * user, with exactly their permissions, so this is the screen where they take that access back.
 * Revoking is immediate: the MCP guard re-checks the grant on every request.
 */
export const ConnectedApps: FunctionComponent = () => {
	const { t, i18n } = useTranslation();
	const ts = (key: string) => t(`account.connectedApps.${key}`);

	const { data: connections, isLoading } = useGetMcpConnectionsQuery();
	const [revoke, { isLoading: isRevoking }] = useRevokeMcpConnectionMutation();

	const formatDate = (value: string) =>
		new Date(value).toLocaleDateString(i18n.language, {
			year: "numeric",
			month: "short",
			day: "numeric",
		});

	return (
		<SettingsCard icon={faPlug} subtitle={ts("subtitle")} title={ts("title")}>
			{isLoading ? (
				<div className="flex justify-center px-5 py-6">
					<Spinner color="#4F46E5" />
				</div>
			) : !connections?.length ? (
				<p className="px-5 py-5 text-[13px] text-text-meta">{ts("empty")}</p>
			) : (
				<ul className="divide-y divide-gray-100">
					{connections.map((connection) => (
						<li
							key={connection.clientId}
							className="flex items-center justify-between gap-4 px-5 py-4"
						>
							<div className="min-w-0">
								<p className="truncate text-[13.5px] font-semibold text-text">
									{connection.clientName}
								</p>
								<p className="mt-0.5 text-[12.5px] text-text-meta">
									{t("account.connectedApps.connectedOn", {
										date: formatDate(connection.connectedAt),
									})}
								</p>
							</div>

							<Button
								color={ButtonColor.DANGER_SECONDARY}
								disabled={isRevoking}
								size={ButtonSize.S}
								testId={`revoke-mcp-${connection.clientId}`}
								title={ts("revoke")}
								onClick={() => revoke(connection.clientId)}
							/>
						</li>
					))}
				</ul>
			)}
		</SettingsCard>
	);
};
